import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import { usePermissions } from "../../lib/permissions";
import { EmptyState, SectionLoading } from "../../components/state";
import { PendingRequestRow, type EaMemberOption } from "./PendingRequestRow";
import { UnlinkedMemberRow, type UnlinkedMember } from "./UnlinkedMemberRow";

type PlayerLinkRequest = {
  id: string;
  user_id: string;
  email: string | null;
  user_first_name: string | null;
  user_last_name: string | null;
  player_id: string;
  player_display_name: string | null;
  player_ea_handle: string | null;
  note: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled";
  requested_at: string;
};

export function RequestsView({ teamId }: { teamId: string }) {
  const { t } = useTranslation("requests");
  const queryClient = useQueryClient();
  const { canManage } = usePermissions();

  const requestsQuery = useQuery({
    queryKey: ["join-requests", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_team_join_requests", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        user_id: string;
        email: string | null;
        first_name: string | null;
        last_name: string | null;
        full_name: string | null;
        note: string | null;
        status: "pending" | "approved" | "rejected" | "cancelled";
        requested_at: string;
        linked_player_id: string | null;
      }>;
    },
  });

  const playersQuery = useQuery({
    queryKey: ["players-unlinked", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, last_name, ea_sports_id, ea_account_handle, user_id")
        .eq("team_id", teamId)
        .eq("status", "active")
        .order("display_name");
      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        display_name: string;
        last_name: string | null;
        ea_sports_id: string | null;
        ea_account_handle: string | null;
        user_id: string | null;
      }>;
    },
  });

  // Membri EA del Mondo FC del team. Vuoto se il club non e' agganciato/sincronizzato.
  const eaMembersQuery = useQuery({
    queryKey: ["ea-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_ea_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Array<{ ea_persona: string; ea_name: string | null; data: any }>;
    },
  });

  const players = playersQuery.data || [];
  const eaMembers = eaMembersQuery.data || [];

  // Set degli handle EA gia' agganciati a un player rosa, per disabilitarli nel select.
  const linkedHandles = new Set<string>();
  for (const p of players) {
    if (p.ea_account_handle) linkedHandles.add(p.ea_account_handle.toLowerCase());
    else if (p.ea_sports_id) linkedHandles.add(p.ea_sports_id.toLowerCase());
  }
  const eaMemberOptions: EaMemberOption[] = eaMembers.map((m) => ({
    ea_persona: m.ea_persona,
    ea_name: m.ea_name,
    pro_name: (m.data?.proName as string | undefined) ?? null,
    alreadyLinked: linkedHandles.has(m.ea_persona.toLowerCase()),
  }));

  const approve = useMutation({
    mutationFn: async ({ requestId, playerId }: { requestId: string; playerId: string | null }) => {
      const { error } = await supabaseV2.rpc("approve_join_request", {
        _request_id: requestId,
        _player_id: playerId,
        _role: "player",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["join-requests", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-unlinked", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["pending-join-requests-count"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const approveCreate = useMutation({
    mutationFn: async ({
      requestId,
      first_name,
      last_name,
      role_code,
      shirt_number,
      ea_account_handle,
    }: {
      requestId: string;
      first_name: string;
      last_name: string;
      role_code: string | null;
      shirt_number: number | null;
      ea_account_handle: string | null;
    }) => {
      const { data: inserted, error: insErr } = await supabaseV2
        .from("players")
        .insert({
          team_id: teamId,
          first_name,
          last_name,
          role_code,
          shirt_number,
          ea_account_handle,
          status: "active",
          is_guest: false,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;
      const { error: apprErr } = await supabaseV2.rpc("approve_join_request", {
        _request_id: requestId,
        _player_id: (inserted as any).id,
        _role: "player",
      });
      if (apprErr) throw apprErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["join-requests", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-unlinked", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["pending-join-requests-count"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const reject = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabaseV2.rpc("reject_join_request", { _request_id: requestId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["join-requests", teamId] });
      queryClient.invalidateQueries({ queryKey: ["pending-join-requests-count"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const playerLinkQuery = useQuery({
    queryKey: ["player-link-requests", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_player_link_requests", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as PlayerLinkRequest[];
    },
  });

  const approvePlayerLink = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabaseV2.rpc("approve_player_link_request", { _request_id: requestId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-link-requests", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-unlinked", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const rejectPlayerLink = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabaseV2.rpc("reject_player_link_request", { _request_id: requestId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-link-requests", teamId] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  // Membri team senza player rosa collegato (es. entrati via link invito).
  const unlinkedQuery = useQuery({
    queryKey: ["unlinked-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_unlinked_team_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as UnlinkedMember[];
    },
  });

  const linkExisting = useMutation({
    mutationFn: async ({ playerId, userId }: { playerId: string; userId: string }) => {
      const target = players.find((p) => p.id === playerId);
      const member = (unlinkedQuery.data || []).find((m) => m.user_id === userId);
      const isStub = !!target && (!target.last_name || target.last_name.trim() === "");
      const inheritNames = isStub && member && member.first_name && member.last_name;
      const update: Record<string, any> = { user_id: userId };
      if (inheritNames) {
        update.first_name = member.first_name;
        update.last_name = member.last_name;
      }
      const { error } = await supabaseV2.from("players").update(update).eq("id", playerId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-unlinked", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const createAndLink = useMutation({
    mutationFn: async ({
      userId,
      first_name,
      last_name,
      role_code,
      shirt_number,
      ea_account_handle,
      as_guest,
    }: {
      userId: string;
      first_name: string;
      last_name: string;
      role_code: string | null;
      shirt_number: number | null;
      ea_account_handle: string | null;
      as_guest: boolean;
    }) => {
      const { error } = await supabaseV2.from("players").insert({
        team_id: teamId,
        user_id: userId,
        first_name,
        last_name,
        role_code,
        shirt_number,
        ea_account_handle,
        status: "active",
        is_guest: as_guest,
        // Provinante -> in valutazione; giocatore di rosa -> default colonna.
        ...(as_guest ? { tryout_status: "evaluating" } : {}),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-unlinked", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => alert(t("errors.generic", { message: e.message || String(e) })),
  });

  const dismissUnlinked = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabaseV2.rpc("dismiss_unlinked_team_member", {
        _team_id: teamId,
        _user_id: userId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unlinked-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["unlinked-members-count"] });
    },
    onError: (e: any) => {
      const msg = e?.message || String(e);
      console.error("[dismissUnlinked]", e);
      alert(t("errors.generic", { message: msg }));
    },
  });

  if (!canManage) {
    return (
      <Card>
        <CardContent className="p-6">
          <EmptyState text={t("noPermission")} />
        </CardContent>
      </Card>
    );
  }

  const requests = requestsQuery.data || [];
  const pending = requests.filter((r) => r.status === "pending");
  const others = requests.filter((r) => r.status !== "pending");
  const unlinked = unlinkedQuery.data || [];
  const playerLinkPending = (playerLinkQuery.data || []).filter((r) => r.status === "pending");

  const isWorking =
    approve.isPending || approveCreate.isPending || reject.isPending ||
    linkExisting.isPending || createAndLink.isPending || dismissUnlinked.isPending ||
    approvePlayerLink.isPending || rejectPlayerLink.isPending;

  return (
    <div className="space-y-4 animate-fade-in">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("pending.title", { count: pending.length })}</CardTitle>
          <CardDescription>
            {t("pending.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {requestsQuery.isLoading && <SectionLoading />}
          {!requestsQuery.isLoading && pending.length === 0 && (
            <EmptyState text={t("pending.empty")} />
          )}
          {pending.map((r) => (
            <PendingRequestRow
              key={r.id}
              request={r}
              players={players}
              eaMembers={eaMemberOptions}
              onApproveExisting={(playerId) => approve.mutate({ requestId: r.id, playerId })}
              onApproveCreate={(payload) =>
                approveCreate.mutate({ requestId: r.id, ...payload })
              }
              onReject={() => {
                if (window.confirm(t("rejectConfirm"))) reject.mutate(r.id);
              }}
              isWorking={isWorking}
            />
          ))}
        </CardContent>
      </Card>

      {playerLinkPending.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("playerLink.title", { count: playerLinkPending.length })}</CardTitle>
            <CardDescription>{t("playerLink.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {playerLinkPending.map((r) => {
              const userName = [r.user_first_name, r.user_last_name]
                .filter((x) => !!x && x.trim())
                .join(" ")
                .trim() || r.email || r.user_id.slice(0, 8);
              const playerName =
                r.player_display_name?.trim() || r.player_ea_handle || "—";
              return (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center gap-2 px-3 py-2.5 border rounded-lg bg-card"
                >
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm">
                      <Trans
                        i18nKey="playerLink.claimLine"
                        t={t}
                        values={{ user: userName, player: playerName }}
                        components={{ strong: <strong /> }}
                      />
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.email && <span className="mr-2">{r.email}</span>}
                      <span>
                        {t("playerLink.requestedAt", {
                          date: new Date(r.requested_at).toLocaleDateString(currentIntlLocale()),
                        })}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="default"
                      className="h-8 text-xs"
                      disabled={isWorking}
                      onClick={() => approvePlayerLink.mutate(r.id)}
                    >
                      {approvePlayerLink.isPending ? t("playerLink.approving") : t("playerLink.approve")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                      disabled={isWorking}
                      onClick={() => {
                        if (window.confirm(t("playerLink.rejectConfirm"))) {
                          rejectPlayerLink.mutate(r.id);
                        }
                      }}
                    >
                      {rejectPlayerLink.isPending ? t("playerLink.rejecting") : t("playerLink.reject")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {unlinked.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("unlinked.title", { count: unlinked.length })}</CardTitle>
            <CardDescription>
              {t("unlinked.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {unlinked.map((m) => (
              <UnlinkedMemberRow
                key={m.user_id}
                member={m}
                players={players}
                eaMembers={eaMemberOptions}
                onLinkExisting={(playerId) =>
                  linkExisting.mutate({ playerId, userId: m.user_id })
                }
                onCreateAndLink={(payload) =>
                  createAndLink.mutate({ userId: m.user_id, ...payload })
                }
                onDismiss={() => dismissUnlinked.mutate(m.user_id)}
                isWorking={isWorking}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {others.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("history.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {others.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-3 py-2 border rounded-md bg-card text-sm">
                <span className={cn(
                  "tag-mono",
                  r.status === "approved" && "bg-emerald-50 text-emerald-700 border-emerald-200",
                  r.status === "rejected" && "bg-rose-50 text-rose-700 border-rose-200",
                  r.status === "cancelled" && "bg-slate-50 text-slate-600 border-slate-200"
                )}>{t(`history.status.${r.status}`, { defaultValue: r.status })}</span>
                <span className="truncate flex-1">
                  {r.full_name && r.full_name !== r.email
                    ? <><span className="font-medium">{r.full_name}</span> <span className="text-muted-foreground">· {r.email}</span></>
                    : (r.email || r.user_id.slice(0, 8) + "…")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(r.requested_at).toLocaleDateString(currentIntlLocale())}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
